/**
 * Single source of truth for automation indicator label and tooltip copy.
 * Maps (automationType, isStopped, stopReason) to { label, tooltip }.
 * Used by ChatSection; translate labels/tooltips with __() at render time.
 *
 * Backend stop reasons (FeedbackAiChatStopReason): escalation, manual_stop,
 * human_intervention, conversation_concluded.
 */

export const AUTOMATION_INDICATOR_LABELS = {
    full_active: 'Fully-Automated (active)',
    full_stopped_manual: 'Fully-Automated (stopped - manual intervention)',
    full_stopped_escalation: 'Fully-Automated (stopped - escalation)',
    semi_active: 'Semi-Automated (active)',
    semi_stopped_manual: 'Semi-Automated (stopped - manual intervention)',
    semi_stopped_initial_chat: 'Semi-Automated (stopped - initial chat sent)',
};

export const AUTOMATION_INDICATOR_TOOLTIPS = {
    full_active: 'This conversation is currently in fully-automated mode and will automatically be responded to by our system using AI unless an escalation event is detected or there is a manual intervention',
    full_stopped_manual: 'This conversation is no longer in fully-automated mode due to manual intervention',
    full_stopped_escalation: 'This conversation is no longer in fully-automated mode due to detecting an escalation event',
    semi_active: 'This conversation is currently in semi-automated mode and will only respond automatically for the initial response',
    semi_stopped_manual: 'This conversation is no longer in semi-automated mode due to manual intervention',
    semi_stopped_initial_chat: 'This conversation is no longer in semi-automated mode due to an initial chat message already have been sent',
};

/**
 * Normalize stop reason so we accept both string value and object (e.g. enum with .value).
 * @param {string|{ value?: string }|null} stopReason
 * @returns {string|null}
 */
function normalizeStopReason(stopReason) {
    if (stopReason == null) return null;
    if (typeof stopReason === 'string') return stopReason;
    return stopReason?.value ?? null;
}

/**
 * @param {'full'|'semi'|string} automationType
 * @param {boolean} isStopped
 * @param {string|{ value?: string }|null} stopReason - escalation | manual_stop | human_intervention | conversation_concluded (string or enum-like object)
 * @returns {{ label: string, tooltip: string, key: string }}
 */
export function getAutomationIndicatorConfig(automationType, isStopped, stopReason) {
    const reason = normalizeStopReason(stopReason);
    const full = automationType === 'full';
    const semi = automationType === 'semi';

    if (!isStopped) {
        const key = full ? 'full_active' : semi ? 'semi_active' : null;
        const label = key ? AUTOMATION_INDICATOR_LABELS[key] : `Manual (active)`;
        const tooltip = key ? AUTOMATION_INDICATOR_TOOLTIPS[key] : '';
        return { label, tooltip, key: key || 'manual_active' };
    }

    if (reason === 'escalation' && full) {
        return {
            label: AUTOMATION_INDICATOR_LABELS.full_stopped_escalation,
            tooltip: AUTOMATION_INDICATOR_TOOLTIPS.full_stopped_escalation,
            key: 'full_stopped_escalation',
        };
    }
    if (reason === 'conversation_concluded' && semi) {
        return {
            label: AUTOMATION_INDICATOR_LABELS.semi_stopped_initial_chat,
            tooltip: AUTOMATION_INDICATOR_TOOLTIPS.semi_stopped_initial_chat,
            key: 'semi_stopped_initial_chat',
        };
    }
    if (reason === 'manual_stop' || reason === 'human_intervention') {
        const key = full ? 'full_stopped_manual' : 'semi_stopped_manual';
        return {
            label: AUTOMATION_INDICATOR_LABELS[key],
            tooltip: AUTOMATION_INDICATOR_TOOLTIPS[key],
            key,
        };
    }

    // Fallbacks:
    // - For SEMI + stopped but no explicit reason yet (e.g. countdown reached 0 and initial chat is being sent),
    //   prefer \"initial chat sent\" state over \"manual intervention\".
    if (semi) {
        return {
            label: AUTOMATION_INDICATOR_LABELS.semi_stopped_initial_chat,
            tooltip: AUTOMATION_INDICATOR_TOOLTIPS.semi_stopped_initial_chat,
            key: 'semi_stopped_initial_chat',
        };
    }

    const key = full ? 'full_stopped_manual' : 'semi_stopped_manual';
    return {
        label: AUTOMATION_INDICATOR_LABELS[key],
        tooltip: AUTOMATION_INDICATOR_TOOLTIPS[key],
        key,
    };
}
